'use client';

import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from 'react';

import { ConfirmFn, ConfirmOptions } from '../config';
import { Confirm } from '../ui';

const ConfirmContext = createContext<ConfirmFn>(() => {
	throw new Error('ConfirmProvider is missing');
});

export function useConfirm() {
	return useContext(ConfirmContext);
}

export function ConfirmProvider({ children }: React.PropsWithChildren) {
	const [open, setIsOpen] = useState(false);
	const resolver = useRef<(value: boolean) => void>(null);

	const [dialogContent, setDialogContent] = useState<{
		message: string;
		options?: ConfirmOptions;
	}>({ message: '', options: {} });

	const confirm: ConfirmFn = useCallback((message, options) => {
		setDialogContent({ message, options });
		setIsOpen(true);
		return new Promise<boolean>((resolve) => {
			resolver.current = resolve;
		});
	}, []);

	const onConfirm = () => {
		setIsOpen(false);
		resolver.current?.(true);
	};

	const onClose = () => {
		setIsOpen(false);
		resolver.current?.(false);
	};

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}

			<Confirm
				open={open}
				onClose={onClose}
				onConfirm={onConfirm}
				options={dialogContent.options}
			/>
		</ConfirmContext.Provider>
	);
}
